import React, { useState, useEffect } from 'react';
import { STANDARD_EVENTS, fireEvent, Product } from '../constants';
import { ShoppingCart, UserCog, Info, Play, Activity, AlertCircle, CheckCircle2, History, Package, ExternalLink, DollarSign, Scale, Plus, Trash2, X, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface LogEntry {
  id: string;
  timestamp: string;
  type: string;
  payload: any;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  gender: string;
  country: string;
}

export const Installer: React.FC = () => {
  const { t } = useLanguage();
  // Configuration State
  const [pixelId, setPixelId] = useState('1560233468582382');
  const [eventType, setEventType] = useState('Purchase');
  
  const [userData, setUserData] = useState({
    fn: '',
    ln: '',
    ph: '',
    ge: '',
    cn: 'LY' // Country always set to LY (Libya)
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [eventData, setEventData] = useState({
    priceSoldLYD: '',
    currency: 'USD',
    contentName: ''
  });

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Product Management State
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    purchase_value_usd: '',
    estimated_weight_kg: '',
    shipping_cost_per_kg_usd: '',
    purchase_link: ''
  });

  // Customer Management State
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    gender: '',
    country: 'LY'
  });

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await fetch('/api/products');
        const data = await response.json();
        if (data.success) {
          setProducts(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const response = await fetch('/api/customers');
        const data = await response.json();
        if (data.success) {
          setCustomers(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  // Handlers
  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setUserData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProductSelect = (productId: string) => {
    const product = products.find(p => p.id === parseInt(productId));
    setSelectedProduct(product || null);
    if (product) {
      setEventData(prev => ({
        ...prev,
        priceSoldLYD: '',
        contentName: product.name
      }));
    } else {
      setEventData(prev => ({
        ...prev,
        contentName: ''
      }));
    }
  };

  const handleEventChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setEventData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const calculateValueUSD = () => {
    if (!selectedProduct || !eventData.priceSoldLYD) return '';
    const priceSoldLYD = parseFloat(eventData.priceSoldLYD);
    const revenueUSD = priceSoldLYD / 10;
    const shippingCostUSD = selectedProduct.estimated_weight_kg * selectedProduct.shipping_cost_per_kg_usd;
    const totalCostUSD = selectedProduct.purchase_value_usd + shippingCostUSD;
    const grossMarginUSD = revenueUSD - totalCostUSD;
    
    // Debug logging
    console.log('Gross Margin Calculation:', {
      priceSoldLYD,
      revenueUSD,
      purchaseValueUSD: selectedProduct.purchase_value_usd,
      estimatedWeightKg: selectedProduct.estimated_weight_kg,
      shippingCostPerKgUSD: selectedProduct.shipping_cost_per_kg_usd,
      shippingCostUSD,
      totalCostUSD,
      grossMarginUSD
    });
    
    return grossMarginUSD.toFixed(2);
  };

  const handleSendEvent = () => {
    setError(null);
    try {
      if (!pixelId.trim()) {
        setError(t.enterValidPixelId);
        return;
      }

      const calculatedValue = calculateValueUSD();
      if (!calculatedValue) {
        setError(t.selectProductAndPrice);
        return;
      }

      const result = fireEvent({
        pixelId,
        eventType,
        userData,
        eventData: {
          ...eventData,
          value: calculatedValue
        }
      });

      const newLog: LogEntry = {
        id: crypto.randomUUID(),
        timestamp: result.timestamp,
        type: result.type,
        payload: result.payload
      };

      setLogs(prev => [newLog, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newProduct.name,
          purchase_value_usd: parseFloat(newProduct.purchase_value_usd),
          estimated_weight_kg: parseFloat(newProduct.estimated_weight_kg),
          shipping_cost_per_kg_usd: parseFloat(newProduct.shipping_cost_per_kg_usd),
          purchase_link: newProduct.purchase_link
        })
      });
      const data = await response.json();
      if (data.success) {
        setProducts(prev => [...prev, data.data]);
        setNewProduct({
          name: '',
          purchase_value_usd: '',
          estimated_weight_kg: '',
          shipping_cost_per_kg_usd: '',
          purchase_link: ''
        });
        setShowAddProduct(false);
      }
    } catch (err) {
      setError(t.failedToAddProduct);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    try {
      const response = await fetch(`/api/products?id=${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setProducts(prev => prev.filter(p => p.id !== id));
        if (selectedProduct?.id === id) {
          setSelectedProduct(null);
          setEventData(prev => ({
            ...prev,
            priceSoldLYD: '',
            contentName: ''
          }));
        }
      }
    } catch (err) {
      setError(t.failedToDeleteProduct);
    }
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;
    try {
      const response = await fetch(`/api/products?id=${selectedProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: selectedProduct.name,
          purchase_value_usd: selectedProduct.purchase_value_usd,
          estimated_weight_kg: selectedProduct.estimated_weight_kg,
          shipping_cost_per_kg_usd: selectedProduct.shipping_cost_per_kg_usd,
          purchase_link: selectedProduct.purchase_link
        })
      });
      const data = await response.json();
      if (data.success) {
        setProducts(prev => prev.map(p => p.id === selectedProduct.id ? data.data : p));
        setSelectedProduct(data.data);
      }
    } catch (err) {
      setError(t.failedToUpdateProduct);
    }
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newCustomer)
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(prev => [data.data, ...prev]);
        setNewCustomer({
          first_name: '',
          last_name: '',
          phone: '',
          gender: '',
          country: 'LY'
        });
        setShowAddCustomer(false);
      }
    } catch (err) {
      setError(t.failedToAddCustomer);
    }
  };

  const handleDeleteCustomer = async (id: number) => {
    try {
      const response = await fetch(`/api/customers?id=${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(prev => prev.filter(c => c.id !== id));
        if (selectedCustomer?.id === id) {
          setSelectedCustomer(null);
          setUserData({
            fn: '',
            ln: '',
            ph: '',
            ge: '',
            cn: 'LY'
          });
        }
      }
    } catch (err) {
      setError(t.failedToDeleteCustomer);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await fetch(`/api/customers?id=${selectedCustomer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: userData.fn,
          last_name: userData.ln,
          phone: userData.ph,
          gender: userData.ge,
          country: userData.cn
        })
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(prev => prev.map(c => c.id === selectedCustomer.id ? data.data : c));
        setSelectedCustomer(data.data);
      }
    } catch (err) {
      setError(t.failedToUpdateCustomer);
    }
  };

  const handleCustomerSelect = (customerId: string) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    setSelectedCustomer(customer || null);
    if (customer) {
      setUserData({
        fn: customer.first_name,
        ln: customer.last_name,
        ph: customer.phone,
        ge: customer.gender,
        cn: customer.country
      });
    }
  };

  const currentEventDesc = STANDARD_EVENTS.find(e => e.value === eventType)?.desc;

  return (
    <div className="bg-neo-white border-4 border-neo-black shadow-neo overflow-hidden">
      <div className="p-6 md:p-8">
         <div className="mb-6 pb-6 border-b-4 border-neo-black flex flex-col md:flex-row md:items-center justify-between gap-4">
           <div>
             <h3 className="text-2xl font-bold text-neo-black font-mono uppercase">{t.liveEventTester}</h3>
             <p className="text-neo-black mt-1 font-mono text-sm">
               {t.liveEventTesterDesc}
             </p>
           </div>
           <div className="bg-neo-pink border-4 border-neo-black px-4 py-2 shadow-neo-sm">
             <label className="text-xs font-bold text-neo-black uppercase tracking-wide font-mono">{t.pixelId}</label>
             <input 
               type="text" 
               placeholder="1234567890" 
               className="bg-neo-white border-4 border-neo-black px-2 py-1 text-sm font-mono text-neo-black focus:outline-none w-32 md:w-40 mt-1"
               value={pixelId}
               onChange={(e) => setPixelId(e.target.value)}
             />
           </div>
         </div>

         {error && (
            <div className="mb-6 bg-neo-red border-4 border-neo-black text-neo-black px-4 py-3 shadow-neo-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-mono font-bold">{error}</span>
            </div>
         )}

         <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column: Configuration Form */}
            <div className="lg:col-span-6 space-y-6">
               
               {/* 1. Customer Information (Advanced Matching) */}
               <div className="space-y-3">
                 <div className="flex items-center justify-between">
                   <label className="flex items-center gap-2 text-sm font-bold text-neo-black font-mono uppercase">
                     <UserCog className="w-4 h-4 text-neo-black" />
                     {t.customerInfo}
                     <span className="ml-auto text-xs font-normal text-neo-black bg-neo-yellow border-4 border-neo-black px-2 py-0.5 shadow-neo-sm font-mono">{t.advancedMatching}</span>
                   </label>
                   <button
                     onClick={() => setShowAddCustomer(!showAddCustomer)}
                     className="bg-neo-green border-4 border-neo-black px-3 py-1 text-sm font-bold font-mono shadow-neo-sm flex items-center gap-1 hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                   >
                     <Plus className="w-4 h-4" />
                     {t.add}
                   </button>
                 </div>
                 
                 {showAddCustomer && (
                   <form onSubmit={handleAddCustomer} className="bg-neo-yellow border-4 border-neo-black p-4 shadow-neo space-y-3">
                     <div className="flex items-center justify-between">
                       <h4 className="font-bold font-mono uppercase">{t.newCustomer}</h4>
                       <button
                         type="button"
                         onClick={() => setShowAddCustomer(false)}
                         className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm"
                       >
                         <X className="w-4 h-4" />
                       </button>
                     </div>
                     <div className="grid grid-cols-2 gap-3">
                       <input
                         type="text"
                         placeholder={t.firstName}
                         value={newCustomer.first_name}
                         onChange={(e) => setNewCustomer({...newCustomer, first_name: e.target.value})}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                         required
                       />
                       <input
                         type="text"
                         placeholder={t.lastName}
                         value={newCustomer.last_name}
                         onChange={(e) => setNewCustomer({...newCustomer, last_name: e.target.value})}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                         required
                       />
                     </div>
                     <input
                       type="tel"
                       placeholder={t.phoneNumber}
                       value={newCustomer.phone}
                       onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                       required
                     />
                     <div className="grid grid-cols-2 gap-3">
                       <select
                         value={newCustomer.gender}
                         onChange={(e) => setNewCustomer({...newCustomer, gender: e.target.value})}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                         required
                       >
                         <option value="">{t.gender}</option>
                         <option value="m">{t.male}</option>
                         <option value="f">{t.female}</option>
                       </select>
                       <input
                         type="text"
                         placeholder={t.country}
                         value={newCustomer.country}
                         onChange={(e) => setNewCustomer({...newCustomer, country: e.target.value})}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                       />
                     </div>
                     <button
                       type="submit"
                       className="w-full bg-neo-blue border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                     >
                       {t.createCustomer}
                     </button>
                   </form>
                 )}
                 
                 <select
                   value={selectedCustomer?.id || ''}
                   onChange={(e) => handleCustomerSelect(e.target.value)}
                   className="w-full px-3 py-2.5 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                 >
                   <option value="">{t.selectCustomer}</option>
                   {customers.map(customer => (
                     <option key={customer.id} value={customer.id}>{customer.first_name} {customer.last_name} ({customer.phone})</option>
                   ))}
                 </select>
                 
                 {selectedCustomer && (
                   <div className="bg-neo-blue border-4 border-neo-black p-4 shadow-neo-sm">
                     <div className="flex items-center justify-between mb-2">
                       <span className="text-sm font-bold text-neo-black font-mono">{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                       <div className="flex items-center gap-2">
                         <button
                           onClick={handleUpdateCustomer}
                           className="bg-neo-green border-4 border-neo-black px-2 py-1 text-xs font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                         >
                           {t.save}
                         </button>
                         <button
                           onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                           className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                         >
                           <Trash2 className="w-3 h-3" />
                         </button>
                       </div>
                     </div>
                     <div className="grid grid-cols-2 gap-3 text-xs">
                       <div className="flex items-center gap-2">
                         <Users className="w-3 h-3 text-neo-black" />
                         <span className="font-mono">{t.phoneNumber}:</span>
                         <span className="font-bold font-mono">{selectedCustomer.phone}</span>
                       </div>
                       <div className="flex items-center gap-2">
                         <Users className="w-3 h-3 text-neo-black" />
                         <span className="font-mono">{t.gender}:</span>
                         <span className="font-bold font-mono">{selectedCustomer.gender === 'm' ? t.male : t.female}</span>
                       </div>
                       <div className="flex items-center gap-2 col-span-2">
                         <Users className="w-3 h-3 text-neo-black" />
                         <span className="font-mono">{t.country}:</span>
                         <span className="font-bold font-mono">{selectedCustomer.country}</span>
                       </div>
                     </div>
                   </div>
                 )}
                 
                 <div className="grid grid-cols-2 gap-3">
                   <div className="col-span-1">
                     <input
                       type="text"
                       name="fn"
                       placeholder={t.firstName}
                       value={userData.fn}
                       onChange={handleUserChange}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                     />
                   </div>
                   <div className="col-span-1">
                     <input
                       type="text"
                       name="ln"
                       placeholder={t.lastName}
                       value={userData.ln}
                       onChange={handleUserChange}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                     />
                   </div>
                   <div className="col-span-2">
                     <input
                       type="tel"
                       name="ph"
                       placeholder={t.phoneNumber}
                       value={userData.ph}
                       onChange={handleUserChange}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                     />
                   </div>
                   <div className="col-span-1">
                     <select
                       name="ge"
                       value={userData.ge}
                       onChange={handleUserChange}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                     >
                       <option value="">{t.gender}</option>
                       <option value="m">{t.male}</option>
                       <option value="f">{t.female}</option>
                     </select>
                   </div>
                   <div className="col-span-1">
                     <input
                       type="text"
                       name="cn"
                       placeholder={t.country}
                       value={userData.cn}
                       readOnly
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-gray text-neo-black cursor-not-allowed font-mono shadow-neo-sm"
                       title="Country is fixed to LY (Libya)"
                     />
                   </div>
                 </div>
               </div>

               {/* 2. Product Selection & Management */}
               <div className="space-y-3">
                 <div className="flex items-center justify-between">
                   <label className="flex items-center gap-2 text-sm font-bold text-neo-black font-mono uppercase">
                     <Package className="w-4 h-4 text-neo-black" />
                     {t.selectProduct}
                   </label>
                   <button
                     onClick={() => setShowAddProduct(!showAddProduct)}
                     className="bg-neo-green border-4 border-neo-black px-3 py-1 text-sm font-bold font-mono shadow-neo-sm flex items-center gap-1 hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                   >
                     <Plus className="w-4 h-4" />
                     {t.add}
                   </button>
                 </div>
                 
                 {showAddProduct && (
                   <form onSubmit={handleAddProduct} className="bg-neo-yellow border-4 border-neo-black p-4 shadow-neo space-y-3">
                     <div className="flex items-center justify-between">
                       <h4 className="font-bold font-mono uppercase">{t.newProduct}</h4>
                       <button
                         type="button"
                         onClick={() => setShowAddProduct(false)}
                         className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm"
                       >
                         <X className="w-4 h-4" />
                       </button>
                     </div>
                     <input
                       type="text"
                       placeholder={t.productName}
                       value={newProduct.name}
                       onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                       required
                     />
                     <div className="grid grid-cols-2 gap-3">
                       <input
                         type="number"
                         step="0.01"
                         placeholder={t.valueUsd}
                         value={newProduct.purchase_value_usd}
                         onChange={(e) => setNewProduct({...newProduct, purchase_value_usd: e.target.value})}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                         required
                       />
                       <input
                         type="number"
                         step="0.01"
                         placeholder={t.weightKg}
                         value={newProduct.estimated_weight_kg}
                         onChange={(e) => setNewProduct({...newProduct, estimated_weight_kg: e.target.value})}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                         required
                       />
                     </div>
                     <input
                       type="number"
                       step="0.01"
                       placeholder={t.shippingCostPerKg}
                       value={newProduct.shipping_cost_per_kg_usd}
                       onChange={(e) => setNewProduct({...newProduct, shipping_cost_per_kg_usd: e.target.value})}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                       required
                     />
                     <input
                       type="url"
                       placeholder={t.purchaseLink}
                       value={newProduct.purchase_link}
                       onChange={(e) => setNewProduct({...newProduct, purchase_link: e.target.value})}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                       required
                     />
                     <button
                       type="submit"
                       className="w-full bg-neo-blue border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                     >
                       {t.createProduct}
                     </button>
                   </form>
                 )}
                 
                 <select
                   value={selectedProduct?.id || ''}
                   onChange={(e) => handleProductSelect(e.target.value)}
                   className="w-full px-3 py-2.5 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                 >
                   <option value="">{t.selectProduct}</option>
                   {products.map(product => (
                     <option key={product.id} value={product.id}>{product.name}</option>
                   ))}
                 </select>
                 
                 {selectedProduct && (
                   <div className="bg-neo-blue border-4 border-neo-black p-4 space-y-3 shadow-neo-sm">
                     <div className="flex items-center justify-between">
                       <span className="text-sm font-bold text-neo-black font-mono">{selectedProduct.name}</span>
                       <div className="flex items-center gap-2">
                         <a
                           href={selectedProduct.purchase_link}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="flex items-center gap-1 text-xs text-neo-black font-mono font-bold hover:underline"
                         >
                           <ExternalLink className="w-3 h-3" />
                           {t.link}
                         </a>
                         <button
                           onClick={handleUpdateProduct}
                           className="bg-neo-green border-4 border-neo-black px-2 py-1 text-xs font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                         >
                           {t.save}
                         </button>
                         <button
                           onClick={() => handleDeleteProduct(selectedProduct.id)}
                           className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                         >
                           <Trash2 className="w-3 h-3" />
                         </button>
                       </div>
                     </div>
                     <div className="space-y-3">
                       <div className="grid grid-cols-2 gap-3">
                         <div className="flex items-center gap-2">
                           <DollarSign className="w-3 h-3 text-neo-black" />
                           <span className="font-mono">{t.valueUsd}:</span>
                           <input
                             type="number"
                             step="0.01"
                             value={selectedProduct.purchase_value_usd}
                             onChange={(e) => setSelectedProduct({...selectedProduct, purchase_value_usd: parseFloat(e.target.value) || 0})}
                             className="w-20 px-2 py-1 border-4 border-neo-black text-xs bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                           />
                         </div>
                         <div className="flex items-center gap-2">
                           <Scale className="w-3 h-3 text-neo-black" />
                           <span className="font-mono">{t.weightKg}:</span>
                           <input
                             type="number"
                             step="0.01"
                             value={selectedProduct.estimated_weight_kg}
                             onChange={(e) => setSelectedProduct({...selectedProduct, estimated_weight_kg: parseFloat(e.target.value) || 0})}
                             className="w-20 px-2 py-1 border-4 border-neo-black text-xs bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                           />
                         </div>
                       </div>
                       <div className="flex items-center gap-2">
                         <DollarSign className="w-3 h-3 text-neo-black" />
                         <span className="font-mono">{t.shippingCostPerKg}:</span>
                         <input
                           type="number"
                           step="0.01"
                           value={selectedProduct.shipping_cost_per_kg_usd}
                           onChange={(e) => setSelectedProduct({...selectedProduct, shipping_cost_per_kg_usd: parseFloat(e.target.value) || 0})}
                           className="w-20 px-2 py-1 border-4 border-neo-black text-xs bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                         />
                         <span className="font-mono">/kg</span>
                         <span className="font-mono ml-auto bg-neo-yellow border-4 border-neo-black px-2 py-0.5 shadow-neo-sm">
                           {t.estTotal} ${((selectedProduct.purchase_value_usd + (selectedProduct.estimated_weight_kg * selectedProduct.shipping_cost_per_kg_usd))).toFixed(2)}
                         </span>
                       </div>
                     </div>
                   </div>
                 )}
               </div>

               {/* 3. Event Parameters */}
               <div className="space-y-3">
                 <label className="flex items-center gap-2 text-sm font-bold text-neo-black font-mono uppercase">
                   <ShoppingCart className="w-4 h-4 text-neo-black" />
                   {t.eventParameters}
                 </label>
                 <div className="space-y-3 bg-neo-gray border-4 border-neo-black p-4 shadow-neo-sm">
                   <div className="grid grid-cols-2 gap-3">
                     <div className="col-span-1">
                       <label className="block text-xs font-bold text-neo-black mb-1 font-mono uppercase">{t.priceSoldLyd}</label>
                       <input
                         type="number"
                         name="priceSoldLYD"
                         placeholder="0.00"
                         value={eventData.priceSoldLYD}
                         onChange={handleEventChange}
                         disabled={!selectedProduct}
                         className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm disabled:bg-neo-gray disabled:cursor-not-allowed"
                       />
                     </div>
                     <div className="col-span-1">
                        <label className="block text-xs font-bold text-neo-black mb-1 font-mono uppercase">{t.grossMarginUsd}</label>
                        <div className="bg-neo-white border-4 border-neo-black px-3 py-2 text-sm font-mono shadow-neo-sm">
                          ${calculateValueUSD() || '0.00'}
                        </div>
                     </div>
                   </div>
                   <div>
                     <label className="block text-xs font-bold text-neo-black mb-1 font-mono uppercase">{t.contentName}</label>
                     <input
                       type="text"
                       name="contentName"
                       placeholder="e.g. Red Sneakers"
                       value={selectedProduct ? selectedProduct.name : eventData.contentName}
                       onChange={handleEventChange}
                       readOnly={!!selectedProduct}
                       className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm disabled:bg-neo-gray disabled:cursor-not-allowed"
                       title={selectedProduct ? "Auto-filled from selected product" : "Enter content name or select a product"}
                     />
                   </div>
                 </div>
               </div>

               <button
                 onClick={handleSendEvent}
                 className="w-full mt-4 flex items-center justify-center gap-2 bg-neo-green border-4 border-neo-black text-neo-black font-bold font-mono py-3 px-4 shadow-neo hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo-lg transition-all uppercase"
               >
                 <Play className="w-5 h-5 fill-current" />
                 {t.sendEventNow}
               </button>

            </div>

            {/* Right Column: Activity Log */}
            <div className="lg:col-span-6 flex flex-col h-full border-l-4 border-neo-black lg:pl-8">
               <div className="flex items-center justify-between mb-4">
                 <h4 className="flex items-center gap-2 text-sm font-bold text-neo-black uppercase tracking-wide font-mono">
                   <Activity className="w-4 h-4 text-neo-black" />
                   {t.activityLog}
                 </h4>
                 <span className="bg-neo-yellow border-4 border-neo-black px-2 py-0.5 text-xs font-mono font-bold shadow-neo-sm">{logs.length} {t.eventsSent}</span>
               </div>
               
               <div className="flex-1 bg-neo-gray border-4 border-neo-black overflow-hidden flex flex-col h-[600px] shadow-neo-sm">
                 {logs.length === 0 ? (
                   <div className="flex-1 flex flex-col items-center justify-center text-neo-black p-8 text-center">
                     <History className="w-12 h-12 mb-3" />
                     <p className="text-sm font-mono font-bold">{t.noEventsFired}</p>
                     <p className="text-xs mt-1 font-mono">{t.noEventsDesc}</p>
                   </div>
                 ) : (
                   <div className="flex-1 overflow-y-auto p-4 space-y-3">
                     {logs.map((log) => (
                       <div key={log.id} className="bg-neo-white border-4 border-neo-black p-3 shadow-neo-sm">
                         <div className="flex items-center justify-between mb-2">
                           <div className="flex items-center gap-2">
                             <CheckCircle2 className="w-4 h-4 text-neo-green" />
                             <span className="font-bold text-sm text-neo-black font-mono">{log.type}</span>
                           </div>
                           <span className="text-xs font-mono text-neo-black">{log.timestamp}</span>
                         </div>
                         <div className="bg-neo-gray border-4 border-neo-black p-2 text-xs font-mono text-neo-black overflow-x-auto shadow-neo-sm">
                           {JSON.stringify(log.payload, null, 2)}
                         </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
            </div>
         </div>
      </div>
    </div>
  );
};
